import React, {ReactNode, useEffect, useState} from 'react';
import {NavLink} from "react-router-dom";
import {ApiService} from "../../service/ApiService";
import {AuthService} from "../../service/AuthService";
import {scrollTo} from "../../service/ToolsService";
import {useAppSelector} from "../../redux/store";
import {user} from "../../models/user";
import {
  dashboardRaceType,
  dashboardResultType,
  dashboardRouteType,
} from "../../types/Api/DashboardResultType";

const DAY: number = 24 * 60 * 60 * 1000;
const ROUTE_HORIZON_DAYS: number = 14;
const REGISTRATION_CLOSING_DAYS: number = 7;

type alertLevel = 'danger' | 'warning' | 'info';

interface alert {
  level: alertLevel;
  icon: string;
  message: ReactNode;
  link: string;
}

const toDate = (date: string): Date => new Date(`${date}T00:00:00`);

const today = (): Date => {
  const now = new Date();

  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

const daysUntil = (date: string): number => Math.round((toDate(date).getTime() - today().getTime()) / DAY);

const formatDate = (date: string, options: Intl.DateTimeFormatOptions = {weekday: 'long', day: 'numeric', month: 'long'}): string => {
  return toDate(date).toLocaleDateString('fr-FR', options);
}

const formatHour = (hour: string): string => hour.substring(0, 5).replace(':', 'h');

const relativeDay = (date: string): string => {
  const days = daysUntil(date);

  if (days === 0) return "aujourd'hui";
  if (days === 1) return 'demain';
  if (days > 1) return `dans ${days} jours`;

  return `il y a ${-days} jour${days < -1 ? 's' : ''}`;
}

const plural = (count: number, word: string): string => `${count} ${word}${count > 1 ? 's' : ''}`;

const buildAlerts = (data: dashboardResultType): alert[] => {
  const alerts: alert[] = [];

  if (AuthService.hasRole('ROLE_USER') && data.user && data.user.unverified > 0) {
    alerts.push({
      level: 'warning',
      icon: 'fa-user-clock',
      message: <>{plural(data.user.unverified, 'compte')} en attente de validation</>,
      link: '/admin/user',
    });
  }

  if (AuthService.hasRole('ROLE_CALENDRIER') && data.race) {
    data.race.toRank.forEach((race) => alerts.push({
      level: 'warning',
      icon: 'fa-ranking-star',
      message: <>Classement à saisir : <strong>{race.label}</strong> ({formatDate(race.rideDate, {day: 'numeric', month: 'short'})})</>,
      link: `/admin/race/participant/${race.raceId}`,
    }));

    data.race.upcoming
      .filter((race) => race.endRegistration !== null && isRegistrationOpen(race) && daysUntil(race.endRegistration) <= REGISTRATION_CLOSING_DAYS)
      .forEach((race) => alerts.push({
        level: 'info',
        icon: 'fa-hourglass-half',
        message: <>Clôture des inscriptions {relativeDay(race.endRegistration as string)} : <strong>{race.label}</strong></>,
        link: `/admin/race/edit/${race.raceId}`,
      }));

    if (data.race.season === null) {
      alerts.push({
        level: 'danger',
        icon: 'fa-calendar-xmark',
        message: <>Aucune saison active</>,
        link: '/admin/season',
      });
    } else if (data.race.season.upcoming === 0) {
      alerts.push({
        level: 'info',
        icon: 'fa-calendar-plus',
        message: <>Plus aucune course à venir sur la saison {data.race.season.label}</>,
        link: '/admin/season',
      });
    }
  }

  if (AuthService.hasRole('ROLE_PARCOURS') && data.route) {
    if (data.route.last === null) {
      alerts.push({
        level: 'danger',
        icon: 'fa-route',
        message: <>Aucune sortie programmée</>,
        link: '/admin/route/route/add',
      });
    } else if (daysUntil(data.route.last) < ROUTE_HORIZON_DAYS) {
      alerts.push({
        level: 'warning',
        icon: 'fa-route',
        message: <>Sorties programmées seulement jusqu'au {formatDate(data.route.last, {day: 'numeric', month: 'long'})}</>,
        link: '/admin/route/route/add',
      });
    }
  }

  if (AuthService.hasRole('ROLE_BUREAU')) {
    if (!data.office) {
      alerts.push({
        level: 'warning',
        icon: 'fa-users-line',
        message: <>Aucun bureau actif</>,
        link: '/admin/office/office',
      });
    } else if (data.office.completed < data.office.total) {
      alerts.push({
        level: 'info',
        icon: 'fa-users-line',
        message: <>{plural(data.office.total - data.office.completed, 'poste')} vacant{data.office.total - data.office.completed > 1 ? 's' : ''} au bureau {data.office.label}</>,
        link: '/admin/office/office',
      });
    }
  }

  if (AuthService.hasRole('ROLE_EQUIPEMENT') && data.equipment && data.equipment.outOfStock > 0) {
    alerts.push({
      level: 'danger',
      icon: 'fa-shirt',
      message: <>{plural(data.equipment.outOfStock, 'taille')} d'équipement en rupture de stock</>,
      link: '/admin/equipment/equipment',
    });
  }

  const order: alertLevel[] = ['danger', 'warning', 'info'];

  return alerts.sort((a, b) => order.indexOf(a.level) - order.indexOf(b.level));
}

const isRegistrationOpen = (race: dashboardRaceType): boolean => {
  if (race.startRegistration === null || race.endRegistration === null) {
    return false;
  }

  return daysUntil(race.startRegistration) <= 0 && daysUntil(race.endRegistration) >= 0;
}

export const Dashboard = () => {
  const me: user | undefined = useAppSelector((state) => state.auth.me);
  const [data, setData] = useState(undefined as undefined | dashboardResultType);
  const [error, setError] = useState(false);

  useEffect(() => {
    const result = ApiService.get(`/_/dashboard`) as Promise<dashboardResultType>;

    result
      .then((r) => setData(r))
      .catch(() => setError(true));
  }, []);

  const can = {
    user: AuthService.hasRole('ROLE_USER'),
    news: AuthService.hasRole('ROLE_ACTUALITE'),
    route: AuthService.hasRole('ROLE_PARCOURS'),
    race: AuthService.hasRole('ROLE_CALENDRIER'),
    equipment: AuthService.hasRole('ROLE_EQUIPEMENT'),
    office: AuthService.hasRole('ROLE_BUREAU'),
    partner: AuthService.hasRole('ROLE_PARTENAIRE'),
    album: AuthService.hasRole('ROLE_ALBUM'),
  };

  const alerts: alert[] = data ? buildAlerts(data) : [];

  return (
    <>
      <div className="px-3 pb-4 __admin_dashboard__">
        <div className="d-flex flex-wrap justify-content-between align-items-end mb-3">
          <div>
            <h4 className="mb-0">Bonjour{me ? ` ${me.username}` : ''} 👋</h4>
            <span className="text-muted text-capitalize">
              {today().toLocaleDateString('fr-FR', {weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'})}
            </span>
          </div>
          {data?.race?.season && (
            <span className="badge rounded-pill bg-primary fs-6 mt-2">Saison {data.race.season.label}</span>
          )}
        </div>

        {error && (
          <div className="alert alert-danger">Impossible de charger le tableau de bord.</div>
        )}

        {!data && !error && (
          <div className="d-flex justify-content-center py-5">
            <div className="spinner-border text-primary" role="status"/>
          </div>
        )}

        {data && (
          <>
            <div className="row g-3 mb-4">
              {can.route && data.route && (
                <Kpi icon="fa-person-biking" title="Prochaine sortie" link="/admin/route/route"
                     value={data.route.next ? formatDate(data.route.next, {weekday: 'short', day: 'numeric', month: 'short'}) : '—'}
                     subtitle={`${plural(data.route.scheduled, 'sortie')} programmée${data.route.scheduled > 1 ? 's' : ''}`}/>
              )}
              {can.race && data.race?.season && (
                <Kpi icon="fa-flag-checkered" title={`Courses ${data.race.season.label}`} link="/admin/season"
                     value={`${data.race.season.total}`}
                     subtitle={`${data.race.season.upcoming} à venir · ${data.race.season.registrationOpen} inscription${data.race.season.registrationOpen > 1 ? 's' : ''} ouverte${data.race.season.registrationOpen > 1 ? 's' : ''}`}/>
              )}
              {can.equipment && data.equipment && (
                <Kpi icon="fa-shirt" title="Équipement en stock" link="/admin/equipment/equipment"
                     value={`${data.equipment.stock}`}
                     subtitle={`${plural(data.equipment.equipments, 'article')} · ${data.equipment.lowStock} taille${data.equipment.lowStock > 1 ? 's' : ''} en stock faible`}
                     tone={data.equipment.outOfStock > 0 ? 'danger' : undefined}/>
              )}
              {can.user && data.user && (
                <Kpi icon="fa-user-secret" title="Utilisateurs actifs" link="/admin/user"
                     value={`${data.user.enabled}`}
                     subtitle={`${data.user.unverified} en attente · ${data.user.disabled} désactivé${data.user.disabled > 1 ? 's' : ''}`}
                     tone={data.user.unverified > 0 ? 'warning' : undefined}/>
              )}
              {can.news && data.news && (
                <Kpi icon="fa-circle-info" title="Actualités en ligne" link="/admin/news"
                     value={`${data.news.enabled}`}
                     subtitle={`${data.news.disabled} masquée${data.news.disabled > 1 ? 's' : ''}`}/>
              )}
              {can.office && (
                <Kpi icon="fa-users-line" title={data.office ? `Bureau ${data.office.label}` : 'Bureau'} link="/admin/office/office"
                     value={data.office ? `${data.office.completed}/${data.office.total}` : '—'}
                     subtitle="postes pourvus"
                     progress={data.office && data.office.total > 0 ? data.office.completed / data.office.total : undefined}/>
              )}
              {can.partner && data.partner && (
                <Kpi icon="fa-handshake" title="Partenaires actifs" link="/admin/partner"
                     value={`${data.partner.enabled}`}
                     subtitle={`${data.partner.disabled} inactif${data.partner.disabled > 1 ? 's' : ''}`}/>
              )}
            </div>

            <div className="row g-3 mb-4">
              <div className="col-12 col-xl-5">
                <Panel icon="fa-list-check" title="À traiter" badge={alerts.length}>
                  {alerts.length === 0 && (
                    <div className="text-center text-muted py-4">
                      <i className="fa-solid fa-circle-check text-success fs-2 mb-2 d-block"/>
                      Rien à signaler, tout est à jour.
                    </div>
                  )}
                  <div className="list-group list-group-flush">
                    {alerts.map((a: alert, i: number) => (
                      <NavLink key={i} to={a.link} onClick={scrollTo}
                               className={`list-group-item list-group-item-action d-flex align-items-center gap-3 __admin_dashboard_alert_${a.level}__`}>
                        <i className={`fa-solid ${a.icon} fa-fw`}/>
                        <span className="flex-grow-1">{a.message}</span>
                        <i className="fa-solid fa-chevron-right text-muted small"/>
                      </NavLink>
                    ))}
                  </div>
                </Panel>
              </div>

              {can.route && data.route && (
                <div className="col-12 col-md-6 col-xl-3">
                  <Panel icon="fa-route" title="Prochaines sorties" link="/admin/route/route">
                    {data.route.upcoming.length === 0 && (
                      <p className="text-muted text-center py-4 mb-0">Aucune sortie programmée.</p>
                    )}
                    <div className="list-group list-group-flush">
                      {data.route.upcoming.map((r: dashboardRouteType) => (
                        <NavLink key={r.routeId} to={`/admin/route/route/edit/${r.routeId}`} onClick={scrollTo}
                                 className="list-group-item list-group-item-action d-flex gap-3 align-items-center">
                          <DateBlock date={r.rideDate}/>
                          <div className="small lh-sm">
                            <div className="fw-bold">{r.circuit ?? 'Circuit à définir'}{r.distance ? ` · ${r.distance} km` : ''}</div>
                            <div className="text-muted">{formatHour(r.meetHour)} · {r.meetPlace}</div>
                          </div>
                        </NavLink>
                      ))}
                    </div>
                  </Panel>
                </div>
              )}

              {can.race && data.race && (
                <div className="col-12 col-md-6 col-xl-4">
                  <Panel icon="fa-flag-checkered" title="Prochaines courses" link="/admin/season">
                    {data.race.upcoming.length === 0 && (
                      <p className="text-muted text-center py-4 mb-0">Aucune course à venir.</p>
                    )}
                    <div className="list-group list-group-flush">
                      {data.race.upcoming.map((race: dashboardRaceType) => (
                        <RaceItem key={race.raceId} race={race}/>
                      ))}
                    </div>
                  </Panel>
                </div>
              )}
            </div>

            <div className="row g-3">
              {can.equipment && data.equipment && data.equipment.alerts.length > 0 && (
                <div className="col-12 col-lg-6">
                  <Panel icon="fa-boxes-stacked" title="Stock faible" link="/admin/equipment/equipment">
                    <table className="table table-sm align-middle mb-0">
                      <tbody>
                      {data.equipment.alerts.map((s) => (
                        <tr key={s.equipmentSizeId}>
                          <td>
                            <NavLink to={`/admin/equipment/equipment/edit/${s.equipmentId}`} onClick={scrollTo}>{s.equipment}</NavLink>
                          </td>
                          <td className="text-muted">{s.size}</td>
                          <td className="text-end">
                            <span className={`badge ${s.stock <= 0 ? 'bg-danger' : 'bg-warning'}`}>
                              {s.stock <= 0 ? 'épuisé' : `${s.stock} restant${s.stock > 1 ? 's' : ''}`}
                            </span>
                          </td>
                        </tr>
                      ))}
                      </tbody>
                    </table>
                  </Panel>
                </div>
              )}

              <div className="col-12 col-lg-6">
                <Panel icon="fa-bolt" title="Actions rapides">
                  <div className="d-flex flex-wrap gap-2 p-3">
                    {can.route && <QuickAction to="/admin/route/route/add" icon="fa-route" label="Programmer une sortie"/>}
                    {can.news && <QuickAction to="/admin/news/add" icon="fa-newspaper" label="Publier une actualité"/>}
                    {can.race && <QuickAction to="/admin/race/add" icon="fa-flag-checkered" label="Ajouter une course"/>}
                    {can.equipment && <QuickAction to="/admin/equipment/equipment/add" icon="fa-shirt" label="Ajouter un équipement"/>}
                    {can.album && <QuickAction to="/admin/album/add" icon="fa-images" label="Créer un album photo"/>}
                    {can.partner && <QuickAction to="/admin/partner/add" icon="fa-handshake" label="Ajouter un partenaire"/>}
                    {can.user && <QuickAction to="/admin/user/add" icon="fa-user-plus" label="Créer un utilisateur"/>}
                  </div>
                </Panel>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

type KpiProps = {
  icon: string;
  title: string;
  value: string;
  subtitle: string;
  link: string;
  tone?: 'warning' | 'danger';
  progress?: number;
}

const Kpi = ({icon, title, value, subtitle, link, tone, progress}: KpiProps) => (
  <div className="col-12 col-sm-6 col-xl-3">
    <NavLink to={link} onClick={scrollTo} className={`card shadow-sm h-100 text-decoration-none __admin_dashboard_kpi__ ${tone ? `__admin_dashboard_kpi_${tone}__` : ''}`}>
      <div className="card-body d-flex align-items-center gap-3">
        <div className="__admin_dashboard_kpi_icon__">
          <i className={`fa-solid ${icon}`}/>
        </div>
        <div className="flex-grow-1 min-w-0">
          <div className="text-muted small text-uppercase fw-semibold">{title}</div>
          <div className="fs-4 fw-bold text-body text-capitalize text-truncate">{value}</div>
          <div className="small text-warning">{subtitle}</div>
          {progress !== undefined && (
            <div className="progress mt-2" style={{height: 5}}>
              <div className={`progress-bar ${progress < 1 ? 'bg-warning' : 'bg-success'}`} style={{width: `${Math.round(progress * 100)}%`}}/>
            </div>
          )}
        </div>
      </div>
    </NavLink>
  </div>
);

type PanelProps = {
  icon: string;
  title: string;
  link?: string;
  badge?: number;
  children: ReactNode;
}

const Panel = ({icon, title, link, badge, children}: PanelProps) => (
  <div className="card shadow-sm h-100">
    <div className="card-header bg-white d-flex align-items-center gap-2">
      <i className={`fa-solid ${icon} __admin_dashboard_card_icon__`}/>
      <span className="fw-bold">{title}</span>
      {badge !== undefined && badge > 0 && <span className="badge rounded-pill bg-warning">{badge}</span>}
      {link && (
        <NavLink to={link} onClick={scrollTo} className="ms-auto small">Tout voir</NavLink>
      )}
    </div>
    <div className="card-body p-0">
      {children}
    </div>
  </div>
);

const DateBlock = ({date}: { date: string }) => (
  <div className="text-center __admin_dashboard_date__">
    <div className="small text-uppercase">{formatDate(date, {weekday: 'short'}).replace('.', '')}</div>
    <div className="fs-5 fw-bold lh-1">{formatDate(date, {day: 'numeric'})}</div>
    <div className="small text-uppercase">{formatDate(date, {month: 'short'}).replace('.', '')}</div>
  </div>
);

const RaceItem = ({race}: { race: dashboardRaceType }) => {
  const open: boolean = isRegistrationOpen(race);
  const upcoming: boolean = race.startRegistration !== null && daysUntil(race.startRegistration) > 0;
  const fill: number | undefined = race.participantLimit ? Math.min(race.participants / race.participantLimit, 1) : undefined;

  return (
    <NavLink to={`/admin/race/participant/${race.raceId}`} onClick={scrollTo}
             className="list-group-item list-group-item-action d-flex gap-3 align-items-center">
      <DateBlock date={race.rideDate}/>
      <div className="small lh-sm flex-grow-1">
        <div className="fw-bold">{race.label}</div>
        <div className="d-flex flex-wrap gap-1 my-1">
          {open && <span className="badge bg-success">Inscriptions ouvertes</span>}
          {upcoming && <span className="badge bg-info">Inscriptions le {formatDate(race.startRegistration as string, {day: 'numeric', month: 'short'})}</span>}
          {!open && !upcoming && race.endRegistration !== null && <span className="badge bg-warning">Inscriptions closes</span>}
          {race.subsidized && <span className="badge bg-primary">Subventionnée</span>}
        </div>
        <div className="text-muted">
          {race.participantLimit ? `${race.participants}/${race.participantLimit} participants` : plural(race.participants, 'participant')}
        </div>
        {fill !== undefined && (
          <div className="progress mt-1" style={{height: 4}}>
            <div className={`progress-bar ${fill >= 1 ? 'bg-danger' : 'bg-primary'}`} style={{width: `${Math.round(fill * 100)}%`}}/>
          </div>
        )}
      </div>
    </NavLink>
  );
}

const QuickAction = ({to, icon, label}: { to: string, icon: string, label: string }) => (
  <NavLink to={to} onClick={scrollTo} className="btn btn-outline-primary btn-sm">
    <i className={`fa-solid ${icon} me-2`}/>{label}
  </NavLink>
);
